import React, { FormEvent, useState } from 'react';
import {
    Button,
    Dropdown,
    Group,
    InformationType,
    TextInput,
    Text,
    TextType,
    Heading,
    Level,
    ProgressIndicator,
    ProgressType,
    Screen,
    ThemeProvider,
    themes,
    Logo,
    Icon,
    TextInputMode,
    CheckCircleIcon,
} from '@defa/defa-component-library';
import { isAndroid, isIOS } from '@utils/detect-device';
import { CountryType, getCountryCodeValueList } from '@utils/country-list';
import { updatePhoneNumberWithCountry, validateCombinedPhoneNumber } from '@utils/phone-number';
import {
    AndroidDownload,
    FormContainer,
    IconContainer,
    IOSDownload,
    PhoneRow,
} from './anonymous-invite-home.styles';
import i18n from '@i18n';
import { AnonymousSetupResponseStatus } from '@generated/admin-prime/types.gen';
import {
    getChargeSystemSetupInfoQueryKey,
    useAcceptHomeChargeSystemSetupMutation,
} from '@generated/admin-prime/@tanstack/react-query.gen';
import { getChargeSystemSetupInfo } from '@generated/admin-prime/sdk.gen';
import { useQuery } from '@tanstack/react-query';
import { useSupportedCountries } from '../../shared/countries/countries.hooks';
import { useSearchParams } from 'react-router-dom';

/** Loading the invite failed; `status` is the HTTP status when the server answered at all. */
class SetupInfoRequestFailed extends Error {
    constructor(readonly status?: number) {
        super(`Loading the home invite failed${status ? ` with HTTP ${status}` : ''}`);
    }
}

// An invalid, expired or used token is a 4xx, and asking again will not change the answer, so
// show the error page straight away. Everything else keeps React Query's default of three
// retries: server errors, network failures, and a connection lost while reading a 200 (the client
// then returns the error with that 200 response, so the status alone is not a 4xx test).
const retryUnlessClientError = (failureCount: number, error: Error) =>
    !(
        error instanceof SetupInfoRequestFailed &&
        error.status !== undefined &&
        error.status >= 400 &&
        error.status < 500
    ) && failureCount < 3;

function AppIcons() {
    const unknownPlatform = !isIOS() && !isAndroid();
    return (
        <IconContainer>
            {(isIOS() || unknownPlatform) && (
                <IOSDownload
                    href="https://apps.apple.com/se/app/defa-power/id6443797468"
                    target="_blank"
                    rel="noopener noreferrer"
                />
            )}
            {(isAndroid() || unknownPlatform) && (
                <AndroidDownload
                    href="https://play.google.com/store/apps/details?id=com.defa.powerapp&pcampaignid=web_share"
                    target="_blank"
                    rel="noopener noreferrer"
                />
            )}
        </IconContainer>
    );
}

export const AnonymousInviteHome: React.FunctionComponent = () => {
    const [query] = useSearchParams();
    const token = query.get('token');

    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [selectedCountryCode, setSelectedCountryCode] = useState<string | undefined>();

    const {
        data: chargeSystemSetupInfo,
        error: setupInfoError,
        isPending,
    } = useQuery({
        queryKey: getChargeSystemSetupInfoQueryKey({ path: { token: token ?? '' } }),
        // Not the generated query options: they throw only the response body, so the retry rule
        // could not tell a 400 from a 503.
        queryFn: async ({ signal }) => {
            const { data, error, response } = await getChargeSystemSetupInfo({
                path: { token: token ?? '' },
                signal,
            });
            if (error !== undefined) {
                throw new SetupInfoRequestFailed(response?.status);
            }
            return data;
        },
        enabled: !!token,
        retry: retryUnlessClientError,
    });

    const { countryCodes } = useSupportedCountries();
    const countriesList: CountryType[] = getCountryCodeValueList(countryCodes);
    // Accepting the invite saves this country as the charge system's, so default to the
    // address the setup was created with. Integrators may send only `country` ("SE").
    const setupAddress = chargeSystemSetupInfo?.addressDetails;
    const setupCountryCode = [setupAddress?.countryCode, setupAddress?.country]
        .map((code) => code?.toUpperCase())
        .find((code) => countriesList.some((c) => c.countryCode === code));
    const countryCode = selectedCountryCode ?? setupCountryCode;
    const country = countriesList.find((c) => c.countryCode === countryCode);

    const {
        mutate: acceptInvite,
        data: acceptResponse,
        isPending: loadingSave,
        isError: acceptThrew,
    } = useAcceptHomeChargeSystemSetupMutation();

    const isDone = acceptResponse?.status === AnonymousSetupResponseStatus.SUCCESS;
    const submitFailed = acceptThrew || (acceptResponse != null && !isDone);

    const submit = (event: FormEvent) => {
        event.preventDefault();
        acceptInvite({
            body: {
                phoneNumber: updatePhoneNumberWithCountry(phoneNumber, country?.callingCode ?? ''),
                countryCode: country?.countryCode ?? '',
                token: token ?? '',
            },
        });
    };

    const isPhoneNumberOk = validateCombinedPhoneNumber(country?.callingCode ?? '', phoneNumber);

    const isAlreadyAccepted =
        chargeSystemSetupInfo?.status === AnonymousSetupResponseStatus.ALREADY_ACCEPTED_INVITE;
    const hasError = !token || !!setupInfoError || submitFailed;
    const showForm = !isDone && !hasError && !!chargeSystemSetupInfo && !isAlreadyAccepted;
    const isLoading = !!token && isPending;

    return (
        <ThemeProvider theme={themes[1]}>
            <Screen>
                <FormContainer>
                    <Group width="100%" form noMargin onSubmit={submit}>
                        <Logo variant="defa" size={64} />

                        {isLoading && (
                            <IconContainer>
                                <ProgressIndicator progress={0} type={ProgressType.DONUT_LOADING} />
                            </IconContainer>
                        )}

                        {isAlreadyAccepted && (
                            <Group>
                                <Heading level={Level.h1}>
                                    {i18n.t('AnonymousInviteHome.AlreadyAcceptedHeader')}
                                </Heading>
                                <Text type={TextType.body}>
                                    {i18n.t('AnonymousInviteHome.AlreadyAcceptedBody')}
                                </Text>
                                <AppIcons />
                            </Group>
                        )}

                        {hasError && (
                            <Group>
                                <Heading level={Level.h1}>
                                    {i18n.t('AnonymousInviteHome.ErrorHeader')}
                                </Heading>
                                <Text type={TextType.body}>
                                    {i18n.t('AnonymousInviteHome.ErrorBody')}
                                </Text>
                                <Button
                                    text={i18n.t('Back')}
                                    onClick={() =>
                                        window.location.replace(
                                            'https://www.defa.com/support/defa-power/'
                                        )
                                    }
                                />
                            </Group>
                        )}

                        {isDone && (
                            <>
                                <IconContainer>
                                    <Icon icon={CheckCircleIcon} color="#66BB6A" size={48} />
                                </IconContainer>
                                <Group>
                                    <Heading level={Level.h1}>
                                        {i18n.t('AnonymousInviteHome.SuccessHeader')}
                                    </Heading>
                                    <Text>{i18n.t('AnonymousInviteHome.SuccessBody')}</Text>
                                </Group>
                                <AppIcons />
                            </>
                        )}

                        {showForm && (
                            <>
                                <Group>
                                    <Heading level={Level.h1}>
                                        {i18n.t('AnonymousInviteHome.Header')}
                                    </Heading>
                                    <Text type={TextType.body}>
                                        {i18n.t('AnonymousInviteHome.Body')}
                                    </Text>
                                </Group>
                                <Group>
                                    <PhoneRow>
                                        <Dropdown<string>
                                            name="country"
                                            label={i18n.t('AnonymousInviteHome.CountryLabel')}
                                            placeholder={i18n.t(
                                                'OrganizationSettings.CountryPlaceholder'
                                            )}
                                            onChange={(selectedOption) => {
                                                const normalized = selectedOption.toUpperCase();
                                                setSelectedCountryCode(normalized);
                                            }}
                                            value={countryCode}
                                            disabled={loadingSave}
                                            noWrap
                                            items={countriesList.map((c) => c.countryCode)}
                                            keyExtractor={(option) => option}
                                            labelExtractor={(option) => {
                                                const matched = countriesList.find(
                                                    (c) => c.countryCode === option
                                                );
                                                return matched
                                                    ? `${matched.flag} ${matched.callingCode} ${matched.countryCode}`
                                                    : option;
                                            }}
                                        />
                                        <TextInput
                                            label={i18n.t('AnonymousInviteHome.PhoneNumberLabel')}
                                            name="phone"
                                            value={phoneNumber}
                                            onChange={setPhoneNumber}
                                            mode={TextInputMode.PHONE}
                                            autocomplete="tel-national"
                                            disabled={loadingSave}
                                            informationType={InformationType.ERROR}
                                            message={
                                                isPhoneNumberOk
                                                    ? undefined
                                                    : i18n.t('PhoneNumberInvalid')
                                            }
                                        />
                                    </PhoneRow>
                                    <Group>
                                        <Button
                                            text={i18n.t('AnonymousInviteHome.SubmitButton')}
                                            disabled={!isPhoneNumberOk || loadingSave}
                                            type="submit"
                                            name="login-button"
                                            loading={loadingSave}
                                        />
                                    </Group>
                                </Group>
                            </>
                        )}
                    </Group>
                </FormContainer>
            </Screen>
        </ThemeProvider>
    );
};
